import React, { useEffect, useMemo, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/build/pdf.mjs";
import JSZip from "jszip";
import Dropzone from "./Dropzone";

// Worker local (public/pdf.worker.min.mjs)
pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.mjs`;

function baseName(filename = "document.pdf") {
  return filename.replace(/\.[^/.]+$/, "");
}

export default function PdfToImages({ defaultFormat = "png" }) {
  const [file, setFile] = useState(null);

  // options
  const [format, setFormat] = useState(defaultFormat); // "png" | "jpg"
  const [scale, setScale] = useState(2);

  // states
  const [loading, setLoading] = useState(false);
  const [zipLoading, setZipLoading] = useState(false);

  const [pageCount, setPageCount] = useState(null);
  const [progress, setProgress] = useState(null); // { label, current, total }

  const [pages, setPages] = useState([]); // [{ pageNum, url, filename }]

  // preview
  const [previewPage, setPreviewPage] = useState(1);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // refs cleanup
  const urlsRef = useRef([]); // list pages urls
  const previewRef = useRef(null); // single preview url
  const pdfRef = useRef(null); // keep loaded pdf doc in memory for preview (optional)

  const ext = format === "jpg" ? "jpg" : "png";
  const mime = format === "jpg" ? "image/jpeg" : "image/png";

  const canConvert = useMemo(
    () => !!file && !loading && !zipLoading,
    [file, loading, zipLoading]
  );

  const showZip = useMemo(() => {
    return !!file && typeof pageCount === "number" && pageCount >= 2;
  }, [file, pageCount]);

  const cleanupAllUrls = () => {
    urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    urlsRef.current = [];

    if (previewRef.current) {
      URL.revokeObjectURL(previewRef.current);
      previewRef.current = null;
    }
    setPreviewUrl(null);
  };

  const resetAll = () => {
    cleanupAllUrls();
    setPages([]);
    setProgress(null);
    setPageCount(null);
    setPreviewPage(1);
    setFile(null);
    pdfRef.current = null;
  };

  const onPick = (f) => {
    cleanupAllUrls();
    setPages([]);
    setProgress(null);
    setPageCount(null);
    setPreviewPage(1);
    pdfRef.current = null;
    setFile(f || null);
  };

  // --------- Helpers ---------
  const loadPdf = async (pdfFile) => {
    const buf = await pdfFile.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
    return pdf;
  };

  const renderPageToBlob = async (
    pdf,
    pageNum,
    renderScale,
    outMime,
    jpgQuality
  ) => {
    const page = await pdf.getPage(pageNum);
    const viewport = page.getViewport({ scale: renderScale });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { alpha: false });

    canvas.width = Math.floor(viewport.width);
    canvas.height = Math.floor(viewport.height);

    await page.render({ canvasContext: ctx, viewport }).promise;

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error("Canvas toBlob a échoué"))),
        outMime,
        jpgQuality
      );
    });

    return blob;
  };

  // --------- Detect pages & load preview ---------
  useEffect(() => {
    let cancelled = false;

    async function initPdfAndPreview() {
      if (!file) return;

      setPreviewLoading(true);

      try {
        const pdf = await loadPdf(file);
        if (cancelled) return;

        pdfRef.current = pdf;
        setPageCount(pdf.numPages);

        // safe preview page
        const safePage = Math.min(Math.max(1, previewPage), pdf.numPages);

        const blob = await renderPageToBlob(
          pdf,
          safePage,
          1.1,
          "image/jpeg",
          0.85
        );
        if (cancelled) return;

        const url = URL.createObjectURL(blob);
        if (previewRef.current) URL.revokeObjectURL(previewRef.current);
        previewRef.current = url;
        setPreviewUrl(url);

        if (safePage !== previewPage) setPreviewPage(safePage);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setPageCount(null);
          setPreviewUrl(null);
        }
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }

    initPdfAndPreview();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  // Preview navigation (when previewPage changes)
  useEffect(() => {
    let cancelled = false;

    async function updatePreviewPage() {
      if (!file) return;
      if (!pdfRef.current) return;
      if (typeof pageCount !== "number") return;

      const safePage = Math.min(Math.max(1, previewPage), pageCount);

      setPreviewLoading(true);
      try {
        const pdf = pdfRef.current;

        const blob = await renderPageToBlob(
          pdf,
          safePage,
          1.1,
          "image/jpeg",
          0.85
        );
        if (cancelled) return;

        const url = URL.createObjectURL(blob);
        if (previewRef.current) URL.revokeObjectURL(previewRef.current);
        previewRef.current = url;
        setPreviewUrl(url);

        if (safePage !== previewPage) setPreviewPage(safePage);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }

    updatePreviewPage();

    return () => {
      cancelled = true;
    };
  }, [previewPage, file, pageCount]);

  // --------- Convert all pages to links ---------
  const convertAndList = async () => {
    if (!file) return;

    setLoading(true);
    setProgress(null);
    cleanupAllUrls();
    setPages([]);

    try {
      const pdf = pdfRef.current || (await loadPdf(file));
      pdfRef.current = pdf;
      setPageCount(pdf.numPages);

      const out = [];
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        setProgress({
          label: "Conversion...",
          current: pageNum,
          total: pdf.numPages,
        });

        const jpgQuality = format === "jpg" ? 0.92 : undefined;
        const blob = await renderPageToBlob(
          pdf,
          pageNum,
          scale,
          mime,
          jpgQuality
        );

        const url = URL.createObjectURL(blob);
        urlsRef.current.push(url);

        const filename = `${baseName(file.name)}-page-${pageNum}.${ext}`;
        out.push({ pageNum, url, filename });
      }

      setPages(out);
      setProgress(null);
    } catch (e) {
      console.error(e);
      alert("Erreur pendant la conversion PDF → images.");
      setProgress(null);
    } finally {
      setLoading(false);
    }
  };

  // --------- ZIP (only if pages >= 2) ---------
  const downloadZip = async () => {
    if (!file) return;

    setZipLoading(true);
    setProgress(null);

    try {
      const pdf = pdfRef.current || (await loadPdf(file));
      pdfRef.current = pdf;
      setPageCount(pdf.numPages);

      const zip = new JSZip();
      const folder = zip.folder(`${baseName(file.name)}-${ext}`) || zip;

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        setProgress({
          label: "Création du ZIP...",
          current: pageNum,
          total: pdf.numPages,
        });

        const jpgQuality = format === "jpg" ? 0.92 : undefined;
        const blob = await renderPageToBlob(
          pdf,
          pageNum,
          scale,
          mime,
          jpgQuality
        );

        const filename = `${baseName(file.name)}-page-${pageNum}.${ext}`;
        folder.file(filename, blob);
      }

      setProgress({ label: "Compression ZIP...", current: 0, total: 0 });

      const zipBlob = await zip.generateAsync({ type: "blob" });

      const zipUrl = URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = zipUrl;
      a.download = `${baseName(file.name)}-convert.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();

      setTimeout(() => URL.revokeObjectURL(zipUrl), 1500);
      setProgress(null);
    } catch (e) {
      console.error(e);
      alert("Erreur pendant la création du ZIP.");
      setProgress(null);
    } finally {
      setZipLoading(false);
    }
  };

  // cleanup on unmount
  useEffect(() => {
    return () => cleanupAllUrls();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section style={{ padding: 16 }}>
      <Dropzone
        accept="application/pdf,.pdf"
        multiple={false}
        disabled={loading || zipLoading}
        acceptText="Déposez votre PDF ici"
        helperText="Glissez-déposez un PDF ou cliquez sur “Choisir un fichier”"
        onFiles={(files) => onPick(files?.[0] || null)}
      />

      {file && (
        <p style={{ textAlign: "center", color: "#666", marginTop: 8 }}>
          Fichier sélectionné : <b>{file.name}</b>
        </p>
      )}

      {/* PREVIEW */}
      {previewLoading && (
        <p style={{ textAlign: "center", color: "#666", marginTop: 10 }}>
          Génération de l’aperçu…
        </p>
      )}

      {previewUrl && (
        <div style={{ textAlign: "center", marginTop: 10 }}>
          <img
            src={previewUrl}
            alt={`Aperçu page ${previewPage}`}
            style={{
              maxWidth: 520,
              width: "100%",
              borderRadius: 12,
              border: "1px solid #e6e6e6",
              background: "#fff",
            }}
          />

          {typeof pageCount === "number" && pageCount > 1 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: 10,
                marginTop: 10,
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                onClick={() => setPreviewPage((p) => Math.max(1, p - 1))}
                disabled={previewLoading || previewPage <= 1}
                style={{ minWidth: 120 }}
              >
                ◀ Précédent
              </button>

              <div style={{ alignSelf: "center", color: "#666" }}>
                Page <b>{previewPage}</b> / <b>{pageCount}</b>
              </div>

              <button
                type="button"
                onClick={() =>
                  setPreviewPage((p) => Math.min(pageCount, p + 1))
                }
                disabled={previewLoading || previewPage >= pageCount}
                style={{ minWidth: 120 }}
              >
                Suivant ▶
              </button>
            </div>
          )}
        </div>
      )}

      {/* OPTIONS */}
      <div style={{ maxWidth: 460, margin: "14px auto 0", textAlign: "left" }}>
        <label style={{ display: "block", marginBottom: 10 }}>
          Format
          <select
            value={format}
            onChange={(e) => setFormat(e.target.value)}
            disabled={loading || zipLoading}
            style={{
              width: "100%",
              padding: 10,
              marginTop: 6,
              borderRadius: 5,
              border: "1px solid #ccc",
            }}
          >
            <option value="png">PNG</option>
            <option value="jpg">JPG</option>
          </select>
        </label>

        <label style={{ display: "block" }}>
          Qualité (scale)
          <input
            type="number"
            min={1}
            max={4}
            step={0.5}
            value={scale}
            onChange={(e) => setScale(Number(e.target.value))}
            disabled={loading || zipLoading}
            style={{
              width: "100%",
              padding: 10,
              marginTop: 6,
              borderRadius: 5,
              border: "1px solid #ccc",
            }}
          />
          <small style={{ color: "#666" }}>
            2 = bon compromis, 3+ = plus lourd
          </small>
        </label>

        {typeof pageCount === "number" && (
          <p style={{ marginTop: 10, color: "#666" }}>
            Pages détectées : <b>{pageCount}</b>
          </p>
        )}
      </div>

      {/* ACTIONS */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 12,
          flexWrap: "wrap",
          marginTop: 14,
        }}
      >
        <button
          onClick={convertAndList}
          disabled={!canConvert}
          style={{ minWidth: 180 }}
        >
          {loading ? "Conversion..." : "Convertir"}
        </button>

        {showZip && (
          <button
            onClick={downloadZip}
            disabled={!file || loading || zipLoading}
            style={{ minWidth: 180 }}
          >
            {zipLoading ? "ZIP..." : "Tout télécharger (ZIP)"}
          </button>
        )}

        {file && (
          <button
            type="button"
            onClick={resetAll}
            disabled={loading || zipLoading}
            style={{ minWidth: 180 }}
          >
            Réinitialiser
          </button>
        )}
      </div>

      {/* PROGRESS */}
      {progress?.label && (
        <p style={{ textAlign: "center", marginTop: 12, color: "#666" }}>
          {progress.label}{" "}
          {progress.total ? (
            <b>
              {progress.current}/{progress.total}
            </b>
          ) : null}
        </p>
      )}

      {/* DOWNLOAD LINKS */}
      <div style={{ textAlign: "center", marginTop: 18 }}>
        {pages.map((p) => (
          <div key={p.pageNum} style={{ marginBottom: 8 }}>
            <a href={p.url} download={p.filename}>
              Télécharger {p.filename}
            </a>
          </div>
        ))}
      </div>
    </section>
  );
}
